import React from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useGlobalContext } from '../../../context/GlobalContext';

const HeroSection = () => {
  const { globalSettings: settings, resumeUrl, loading } = useGlobalContext();
  return (
    <section className="bg-background py-16 lg:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-primary/10 pointer-events-none"></div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Professional Photo */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              <div className="w-full max-w-md mx-auto lg:max-w-none">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-primary relative bg-white/5">
                  {loading ? (
                    <div className="absolute inset-0 bg-white/5 animate-pulse rounded-2xl" />
                  ) : (
                    <Image
                      src={settings?.about_hero_pic || "/assets/images/Sudhan_2026_v3.png"}
                      alt="Professional portrait of portfolio owner"
                      className="w-full h-full object-cover animate-fade-in"
                    />
                  )}
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-success/10 rounded-full blur-xl"></div>
            </div>
          </div>

          {/* Hero Content */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium mb-6">
              <Icon name="Sparkles" size={16} className="mr-2" />
              About Me
            </div>
            
            <h1 className="font-heading text-4xl lg:text-5xl xl:text-3xl font-bold text-text-primary mb-6 leading-tight">
              Empowering Minds Through
              <span className="text-accent block">Space, Science & Technology</span>
            </h1>
            
            <p className="text-lg lg:text-xl text-text-secondary mb-8 leading-relaxed whitespace-pre-wrap">
              {settings?.about_hero_desc || "I'm a passionate space science enthusiast and STEM educator, driven by curiosity and a mission to bridge technology with societal impact. From collaborating with NASA and ISRO to leading outreach for the International Astronomy & Astrophysics Competition, I thrive at the intersection of data, discovery, and education."}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">

              <a
                href={resumeUrl || "/assets/Resume/Resume PDF.pdf"}
                target="_blank"
                rel="noopener noreferrer"
                title="Download Sudhan R's Official PDF Resume"
                aria-label="Download the comprehensive resume document"
              >
                <Button 
                  variant="primary" 
                  iconName="Download" 
                  iconPosition="right"
                  className="px-8 py-3"
                >
                  Download Resume
                </Button>
              </a>
              <Link 
                to="/contact"
                title="Navigate to Contact & Availability Page"
                aria-label="Propose a Collaboration or Open Communication"
              >
                <Button 
                  variant="outline" 
                  iconName="MessageCircle" 
                  iconPosition="right"
                  className="px-8 py-3"
                >
                  Let's Connect
                </Button>
              </Link>
              
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/10">
              {(settings?.hero_stats && Array.isArray(settings.hero_stats) && settings.hero_stats.length > 0
                ? settings.hero_stats
                : [
                    { value: "3+", label: "Research Projects" },
                    { value: "20+", label: "STEM Outreach Hours" },
                    { value: "500+", label: "Students Reached" }
                  ]
              ).map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <div className="text-2xl lg:text-3xl font-bold text-text-primary">{stat.value}</div>
                  <div className="text-sm text-text-secondary pr-2 break-words">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;